import { getAuth } from 'firebase/auth';

// Axios request interceptor: attach a fresh Firebase ID token as Bearer auth.
// Extracted from apiClient.js so it can be unit-tested without pulling in
// axios's ESM-only entry into the Jest module graph.
export async function attachFirebaseToken(config) {
  const user = getAuth().currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

// Axios response interceptor (error side): self-heal stale-claim tokens.
//
// Firebase ID tokens embed custom claims (e.g. `admin`) and the SDK caches
// them client-side for up to an hour. The API derives roles from those
// claims, so a permission granted server-side mid-session is invisible —
// every request keeps presenting the old token and 403s until the user
// re-logs-in or the hour rolls over.
//
// On the first 401/403 of a request we force-mint a fresh token (which
// carries current claims) and replay the request once. If the retry still
// fails, the user genuinely lacks access and the original error propagates
// to the caller. The forced mint also fires Firebase's onIdTokenChanged,
// so AuthContext's token/claims state updates in the same stroke.
export function createAuthRefreshInterceptor(client) {
  return async function retryWithFreshClaims(error) {
    const config = error?.config;
    const status = error?.response?.status;
    const user = getAuth().currentUser;

    const isAuthFailure = status === 401 || status === 403;
    if (!config || !isAuthFailure || !user || config._authRetried) {
      return Promise.reject(error);
    }

    let token;
    try {
      token = await user.getIdToken(true);
    } catch (refreshError) {
      // Offline / revoked session — surface the original auth failure.
      return Promise.reject(error);
    }

    config._authRetried = true;
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    return client(config);
  };
}
